import React, { useState, useEffect, useContext } from "react";
import { Form, Spinner, Alert, Badge } from "react-bootstrap";
import MarketplaceServices from "../services/MarketplaceServices";
import UserContext from "../../../contexts/UserContext";

const CategorySelector = ({
  value = [],
  onChange,
  error = null,
  disabled = false,
  required = false,
}) => {
  const { getUserPlan } = useContext(UserContext);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Force single category selection for all users (API requirement)
  const planId = getUserPlan();
  const isMultiple = false; // Always single selection
  const maxSelections = 1; // Always limit to 1 category

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setLoadError(null);

      const response = await MarketplaceServices.getCategories({ all: true });
      console.log(response);
      const categoriesData = response.data || [];
      // Sort by sort_order
      const activeCategories = categoriesData
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      
      // Build hierarchical structure and flatten for display
      const flatCategories = [];
      const categoryMap = new Map();
      
      // First pass: create map of all categories
      activeCategories.forEach(category => {
        categoryMap.set(category.id, {
          ...category,
          children: []
        });
      });
      
      // Second pass: build hierarchy
      const rootCategories = [];
      activeCategories.forEach(category => {
        if (category.parent) {
          const parent = categoryMap.get(category.parent);
          if (parent) {
            parent.children.push(categoryMap.get(category.id));
          }
        } else {
          rootCategories.push(categoryMap.get(category.id));
        }
      });
      
      // Third pass: flatten for display with proper hierarchy
      const flattenCategories = (categories, level = 0, parentPath = '') => {
        categories.forEach(category => {
          // Determine icon based on category properties
          let iconClass = 'las la-folder';
          if (category.icon) {
            // Handle both prefixed and non-prefixed icons
            iconClass = category.icon.startsWith('las ') || category.icon.startsWith('fa ') 
              ? category.icon 
              : `las la-${category.icon}`;
          } else if (level > 0) {
            iconClass = 'las la-folder-open';
          }
          
          // Build full path
          const currentPath = parentPath ? `${parentPath} > ${category.name}` : category.name;
          
          flatCategories.push({
            id: category.id,
            name: category.name,
            slug: category.slug,
            description: category.description,
            icon: iconClass,
            color: category.color || '#6c757d',
            full_path: category.full_path || currentPath,
            level: level,
            parent: category.parent,
            sort_order: category.sort_order,
            is_featured: category.is_featured,
            agent_count: category.agent_count || 0
          });
          
          // Recursively add children
          if (category.children && category.children.length > 0) {
            const sortedChildren = category.children.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            flattenCategories(sortedChildren, level + 1, currentPath);
          }
        });
      };
      
      flattenCategories(rootCategories);
      
      // Sort categories: featured categories first, then by sort_order
      flatCategories.sort((a, b) => {
        // Featured categories go first
        if (a.is_featured && !b.is_featured) return -1;
        if (!a.is_featured && b.is_featured) return 1;
        
        // Then by level (main categories before subcategories)
        if (a.level !== b.level) return a.level - b.level;
        
        // Finally by sort_order
        return (a.sort_order || 0) - (b.sort_order || 0);
      });
      
      setCategories(flatCategories);
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
      setLoadError("Erro ao carregar categorias. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (categoryId, checked) => {
    if (!isMultiple) {
      onChange(checked ? [categoryId] : []);
      return;
    }

    let newValue = [...value];

    if (checked) {
      // Check max selections limit
      if (maxSelections && newValue.length >= maxSelections) {
        return;
      }

      if (!newValue.includes(categoryId)) {
        newValue.push(categoryId);
      }
    } else {
      newValue = newValue.filter((id) => id !== categoryId);
    }

    onChange(newValue);
  };

  const getCategoryById = (id) => {
    return categories.find((cat) => cat.id === id);
  };

  if (loading) {
    return (
      <Form.Group className="mb-3">
        <Form.Label>Categorias {required && "*"}</Form.Label>
        <div className="d-flex align-items-center p-3 border rounded bg-light">
          <Spinner animation="border" size="sm" className="me-2" />
          <span className="text-muted">Carregando categorias...</span>
        </div>
      </Form.Group>
    );
  }

  if (loadError) {
    return (
      <Form.Group className="mb-3">
        <Form.Label>Categorias {required && "*"}</Form.Label>
        <Alert variant="danger" className="mb-0">
          <div className="d-flex justify-content-between align-items-center">
            <span>{loadError}</span>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={loadCategories}
            >
              <i className="las la-redo-alt" /> Tentar Novamente
            </button>
          </div>
        </Alert>
      </Form.Group>
    );
  }

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        Categorias {required && "*"}
        {isMultiple && maxSelections && (
          <span className="text-muted small ms-1">
            (Máximo: {maxSelections})
          </span>
        )}
        <span className="text-muted small ms-1">(Selecione 1 categoria)</span>
      </Form.Label>

      {/* Selected category badge */}
      {value.length > 0 && (
        <div className="mb-2">
          <div className="small text-muted mb-1">Categoria selecionada:</div>
          <div className="d-flex flex-wrap gap-1">
            {value.map((categoryId) => {
              const category = getCategoryById(categoryId);
              if (!category) return null;

              return (
                <Badge
                  key={categoryId}
                  bg="primary"
                  className="d-flex align-items-center"
                  style={{ fontSize: "0.8rem" }}
                >
                  {category.icon && <i className={`${category.icon} me-1`} />}
                  {category.name}
                  {!disabled && (
                    <button
                      type="button"
                      className="btn-close btn-close-white btn-close-sm ms-1"
                      aria-label={`Remover ${category.name}`}
                      onClick={() => handleCategoryChange(categoryId, false)}
                      style={{ fontSize: "0.6rem" }}
                    />
                  )}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {/* Categories list */}
      <div
        className={`marketplace-categories-container border rounded p-3 ${
          error ? "border-danger" : ""
        }`}
      >
        {categories.length === 0 ? (
          <div className="text-muted text-center py-3">
            <i className="las la-info-circle me-1" />
            Nenhuma categoria disponível
          </div>
        ) : (
          <div className="row g-2">
            {categories.map((category) => {
              const isSelected = value.includes(category.id);
              const isDisabled =
                disabled ||
                (isMultiple &&
                  maxSelections &&
                  !isSelected &&
                  value.length >= maxSelections);

              return (
                <div key={category.id} className="col-md-6">
                  <Form.Check
                    type={isMultiple ? "checkbox" : "radio"}
                    id={`category-${category.id}`}
                    name={isMultiple ? undefined : "category"}
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={(e) =>
                      handleCategoryChange(category.id, e.target.checked)
                    }
                    label={
                      <div className="d-flex align-items-center">
                        {category.level > 0 && (
                          <div className="me-2" style={{ width: "20px" }}>
                            <i
                              className="las la-level-down-alt text-muted"
                              style={{ fontSize: "0.9rem" }}
                            />
                          </div>
                        )}
                        
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className={`${
                                category.level > 0 ? "fw-normal" : "fw-bold"
                              }`}
                            >
                              {category.name}
                            </span>
                           {/*  {category.is_featured && (
                              <Badge
                                bg="warning"
                                size="sm"
                                className="text-dark"
                              >
                                <i
                                  className="las la-star me-1"
                                  style={{ fontSize: "0.7rem" }}
                                />
                                Featured
                              </Badge>
                            )} */}
                           {/*  {category.agent_count > 0 && (
                              <Badge bg="secondary" size="sm">
                                {category.agent_count} agente
                                {category.agent_count !== 1 ? "s" : ""}
                              </Badge>
                            )} */}
                          </div>
                          {category.description && (
                            <div className="small text-muted mt-1">
                              {category.description}
                            </div>
                          )}
                          {category.level > 0 && (
                            <div
                              className="small text-muted"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {category.full_path}
                            </div>
                          )}
                        </div>
                      </div>
                    }
                    className={`marketplace-category-item ${
                      category.level > 0 ? "ms-3" : ""
                    }`}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Error display */}
      {error && <div className="invalid-feedback d-block">{error}</div>}

      {/* Help text */}
      <Form.Text className="text-muted">
        <i className="las la-info-circle me-1" />
        Selecione a categoria que melhor descreve seu agente. Apenas uma categoria pode ser selecionada.
      </Form.Text>
    </Form.Group>
  );
};

export default CategorySelector;
